<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <!-- Reset Password v1 -->
      <b-card class="mb-0">
        <!-- Brand logo-->
        <b-link class="brand-logo d-flex" to="/">
          <b-img :src="require('@/assets/images/logo/logo.png')" alt="logo" />
          <h2 class="brand-text text-primary ml-1 mb-0 align-self-center">
            {{ appTitle }}
          </h2>
        </b-link>
        <!-- /Brand logo-->

        <b-card-title class="mb-1"> Reset Password</b-card-title>

        <!-- form -->
        <validation-observer ref="resetPasswordValidation">
          <b-form
            method="POST"
            class="auth-reset-password-form mt-2"
            @submit.prevent="validationForm"
            autocomplete="off"
          >
            <!-- password -->
            <b-form-group label="New Password" label-for="reset-password-new">
              <validation-provider
                #default="{ errors }"
                name="password"
                vid="password"
                rules="required|min:6"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="reset-password-new"
                    v-model="password"
                    :type="password1FieldType"
                    :state="errors.length > 0 ? false : null"
                    class="form-control-merge"
                    name="password"
                    placeholder="············"
                    autocomplete="off"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="password1ToggleIcon"
                      @click="togglePassword1Visibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- confirm password -->
            <b-form-group
              label-for="reset-password-confirm"
              label="Confirm Password"
            >
              <validation-provider
                #default="{ errors }"
                name="password_confirmation"
                vid="password_confirmation"
                rules="required|confirmed:password"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="reset-password-confirm"
                    v-model="passwordConfirmation"
                    :type="password2FieldType"
                    class="form-control-merge"
                    :state="errors.length > 0 ? false : null"
                    name="password_confirmation"
                    placeholder="············"
                    autocomplete="off"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="password2ToggleIcon"
                      @click="togglePassword2Visibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- loading button -->
            <template v-if="isLoading">
              <b-button variant="primary" block disabled>
                <b-spinner small />
                Loading...
              </b-button>
            </template>

            <!-- submit button -->
            <template v-else>
              <b-button block type="submit" variant="primary">
                Set New Password
              </b-button>
            </template>
          </b-form>
        </validation-observer>

        <p class="text-center mt-2">
          <b-link :to="{ name: 'super-admin-login' }">
            <feather-icon icon="ChevronLeftIcon" /> Back to login
          </b-link>
        </p>
      </b-card>
      <!-- /Reset Password v1 -->
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BCard,
  BCardTitle,
  BCardText,
  BForm,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BFormInput,
  BButton,
  BImg,
  BSpinner,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { required, email } from "@validations";
export default {
  name: "ResetPasswordView",
  components: {
    BCard,
    BButton,
    BCardTitle,
    BCardText,
    BForm,
    BFormGroup,
    BInputGroup,
    BLink,
    BFormInput,
    BInputGroupAppend,
    ValidationProvider,
    ValidationObserver,
    BImg,
    BSpinner,
  },
  data() {
    return {
      appTitle: process.env.VUE_APP_TITLE,
      password: "",
      passwordConfirmation: "",
      isLoading: false,

      // Toggle Password
      password1FieldType: "password",
      password2FieldType: "password",
    };
  },
  computed: {
    password1ToggleIcon() {
      return this.password1FieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    password2ToggleIcon() {
      return this.password2FieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },
  methods: {
    togglePassword1Visibility() {
      this.password1FieldType =
        this.password1FieldType === "password" ? "text" : "password";
    },
    togglePassword2Visibility() {
      this.password2FieldType =
        this.password2FieldType === "password" ? "text" : "password";
    },

    validationForm() {
      this.$refs.resetPasswordValidation.validate().then(async (success) => {
        if (!success) return;

        // Grab token from URL query params
        const token = this.$route.query.token;

        if (!token) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: "Invalid password reset link.",
            },
          });
          return;
        }

        try {
          this.isLoading = true;

          // Call backend API
          const res = await this.$api.post("/api/super-admin/reset-password", {
            token,
            password: this.password,
            password_confirmation: this.passwordConfirmation,
          });

          this.isLoading = false;

          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Success",
              icon: "BellIcon",
              variant: "success",
              text: res?.data?.message,
            },
          });

          // Redirect to login after success
          this.$router.push({ name: "super-admin-login" });
        } catch (error) {
          this.isLoading = false;

          let message = "Something went wrong. Please try again.";

          if (error?.response?.data) {
            const data = error.response.data;

            // Laravel validation errors
            if (data.errors) {
              const backendErrors = data.errors;
              const veeErrors = {};
              Object.keys(backendErrors).forEach((field) => {
                veeErrors[field] = backendErrors[field][0]; // only first error
              });
              this.$refs.resetPasswordValidation.setErrors(veeErrors);

              const firstError = Object.values(backendErrors)[0][0];
              if (firstError) message = firstError;
            }

            // Custom single error message from backend
            else if (data.error) {
              message = data.error;
            }

            // Normal message
            else if (data.message) {
              message = data.message;
            }
          }

          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: message,
            },
          });
        }

      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
